import React, { useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import {
	LineChart,
	Line,
	Tooltip as TooltipChart,
	Legend,
	XAxis,
	YAxis,
	ResponsiveContainer,
} from "recharts"
import { Chip } from "@walmart-web/livingdesign-components"
import { CHIP_TRANSLATIONS } from "components/Shared/Timeline/constants"
import ChartContainer from "components/Shared/ChartContainer"
import { formatNumber } from "helpers/numbers"
import {
	METRIC_MAPPER,
	NON_OPTION,
	PRIMARY_COLOR,
	SECONDARY_COLOR,
	SELECTED_METRIC,
} from "components/Transactional/Performance/components/Timeline/constants"
import {
	filtersMapper,
	isMetricValid,
	PRIMARY_TRACK,
	PRIMARY_TRACK_,
	SECONDARY_TRACK,
	SECONDARY_TRACK_,
} from "components/Transactional/Performance/components/Timeline/utils"
import Select from "../Select"
import Tooltip from "../Tooltip"
import { MetricName } from "components/Transactional/Performance/components/MetricsSelect/interfaces"
import "components/Transactional/Performance/components/Timeline/index.scss"
import AsyncMessageResponseHandler from "components/Shared/AsyncMessageResponseHandler"
import Export from "components/Transactional/Performance/components/Timeline/components/ExportCSV"
import { default as CustomLegend } from "components/Transactional/Performance/components/Timeline/components/Legend"
import {
	TimelineChartProps,
	ChipGroupProps,
} from "components/Transactional/Performance/components/Timeline/interfaces"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import { isGrowth } from "../Tooltip/utils"

const ChipGroup: React.FC<ChipGroupProps> = ({ interval, setInterval }) => (
	<div className="chip-group">
		{CHIP_TRANSLATIONS.map((item) => (
			<Chip
				key={`chip-${item.interval}`}
				selected={item.interval === interval}
				onClick={() => setInterval(item.interval)}
			>
				<FormattedMessage id={item.id} />
			</Chip>
		))}
	</div>
)

const TimelineContainer: React.FC<TimelineChartProps> = ({
	level,
	metric,
	timelineMetric,
	interval,
	setInterval,
	timelineData,
	isPendingRequest,
	error,
	isError,
	refetch,
	filters,
	dates,
	template,
	otherMetric = NON_OPTION,
	setOtherMetric = () => {},
	otherIsFetching,
	isErrorOther,
	refetchOther = () => {},
	primaryTrackColor = PRIMARY_COLOR,
	secondaryTrackColor = PRIMARY_COLOR,
	secondaryStrokeDasharray,
}) => {
	const intl = useIntl()
	const haveMetric = isMetricValid(otherMetric)
	const { preset } = useFiltersContext()
	const parsedMetric = METRIC_MAPPER[timelineMetric]

	const [hide, setHide] = useState<Record<string, boolean>>({
		primaryTrack: false,
		secondaryTrack: false,
		primaryTrack_: false,
		secondaryTrack_: false,
	})

	const onClickLegend = (object: any) => {
		setHide({ ...hide, [object.dataKey]: !hide[object.dataKey] })
	}

	useEffect(() => {
		setOtherMetric(NON_OPTION)
	}, [metric])

	const parsedMetricIntl = intl.formatMessage(
		{
			id:
				"phronesis.transactional.application.commercial.benchmark.timeline.metricsOptions",
		},
		{ metric: parsedMetric }
	)

	const labelYAxis = () => {
		return isGrowth(template)
			? intl.formatMessage(
					{
						id:
							"phronesis.transactional.application.commercial.benchmark.timeline.label.growth",
					},
					{ metric: parsedMetricIntl }
			  )
			: parsedMetricIntl
	}

	return (
		<AsyncMessageResponseHandler
			hasData={!!timelineData.length}
			error={error}
			isError={[isError, isErrorOther].some((error) => error)}
			isLoading={[isPendingRequest, otherIsFetching].some((loading) => loading)}
			retry={() => {
				refetch()
				refetchOther()
			}}
		>
			<ChartContainer
				title={{
					id:
						"phronesis.transactional.application.commercial.benchmark.timeline",
					values: {
						metric: parsedMetricIntl,
						variant: (
							<FormattedMessage
								id="phronesis.transactional.application.commercial.benchmark.timeline.growth"
								values={{
									variant: template,
								}}
							/>
						),
					},
				}}
				body={{
					id:
						"phronesis.transactional.application.commercial.benchmark.timeline.body",
					values: {
						metric: parsedMetricIntl,
						preset: (
							<FormattedMessage
								id="phronesis.filters.calendar.preset"
								values={{
									preset,
								}}
							/>
						),
						interval: (
							<FormattedMessage
								id="phronesis.filters.interval"
								values={{
									interval,
								}}
							/>
						),
						variant: (
							<FormattedMessage
								id="phronesis.transactional.application.commercial.benchmark.timeline.growth"
								values={{
									variant: template,
								}}
							/>
						),
						filter: filtersMapper(filters, level),
						b: (value: string) => <b>{value}</b>,
					},
				}}
				className="timeline"
				isLoading={isPendingRequest || otherIsFetching}
				buttons={
					<div className="timeline-buttons">
						<ChipGroup interval={interval} setInterval={setInterval} />
						<Export
							data={timelineData}
							metric={timelineMetric}
							otherMetric={otherMetric}
							interval={interval}
						/>
					</div>
				}
			>
				<Select
					metric={parsedMetric}
					label={intl.formatMessage({
						id:
							"phronesis.transactional.application.commercial.benchmark.timeline.select",
					})}
					value={METRIC_MAPPER[otherMetric as MetricName]}
					setValue={setOtherMetric}
					isVisible={!isGrowth(template)}
				/>
				<ResponsiveContainer width="100%" height={500}>
					<LineChart
						height={500}
						width={300}
						data={timelineData}
						margin={{ right: 12, left: 12 }}
					>
						<YAxis
							yAxisId="left"
							label={{
								value: labelYAxis(),
								angle: -90,
								position: "left",
								style: { textAnchor: "middle" },
							}}
							tick={{ fontSize: 12 }}
							tickFormatter={(value) => formatNumber(value, intl)}
						/>
						<Legend
							content={(props) => (
								<CustomLegend
									{...props}
									onClick={onClickLegend}
									primaryMetric={timelineMetric}
									secondaryMetric={otherMetric}
									dates={dates}
									template={template}
								/>
							)}
						/>
						<XAxis dataKey="interval" />
						<TooltipChart
							content={(props) => (
								<Tooltip
									{...props}
									template={template}
									primaryMetric={SELECTED_METRIC[timelineMetric]}
									secondaryMetric={SELECTED_METRIC[otherMetric]}
								/>
							)}
						/>
						<Line
							dataKey={PRIMARY_TRACK}
							yAxisId="left"
							hide={hide.primaryTrack}
							strokeWidth={2}
							stroke={primaryTrackColor}
						/>
						<Line
							dataKey={SECONDARY_TRACK}
							yAxisId="left"
							hide={hide.secondaryTrack}
							strokeWidth={2}
							stroke={secondaryTrackColor}
							strokeDasharray={secondaryStrokeDasharray}
						/>
						{haveMetric && (
							<React.Fragment>
								<YAxis
									yAxisId="right"
									orientation="right"
									label={{
										value: intl.formatMessage(
											{
												id:
													"phronesis.transactional.application.commercial.benchmark.timeline.metricsOptions",
											},
											{ metric: otherMetric }
										),
										angle: -90,
										position: "right",
										style: { textAnchor: "middle" },
									}}
									tick={{ fontSize: 12 }}
									tickFormatter={(value) => formatNumber(value, intl)}
								/>
								<Line
									dataKey={PRIMARY_TRACK_}
									yAxisId="right"
									hide={hide.primaryTrack_}
									strokeWidth={2}
									stroke={SECONDARY_COLOR}
								/>
								<Line
									dataKey={SECONDARY_TRACK_}
									yAxisId="right"
									hide={hide.secondaryTrack_}
									strokeWidth={2}
									stroke={SECONDARY_COLOR}
									strokeDasharray="5 5"
								/>
							</React.Fragment>
						)}
					</LineChart>
				</ResponsiveContainer>
			</ChartContainer>
		</AsyncMessageResponseHandler>
	)
}

export default TimelineContainer
